import { combineEpics, Epic, ActionsObservable, StateObservable, ofType } from 'redux-observable';
import { RootAction, RootState } from '@src/app/_redux';
import { ApiClient } from '@src/app/_global/utility/apiUtils';
import { ApiConfig } from '@src/app/_global/domain';
import { map, mergeMap, catchError } from 'rxjs/operators';
import { of } from 'rxjs/observable/of';
import { concat } from 'rxjs/observable/concat';
import { fromPromise } from 'rxjs/observable/fromPromise';
import { actionCreators, PAYMENTS_START, PAYMENTS_AUTO_RENEW_START } from '@src/app/accounts/payments/actions';
import { actionCreators as gaActionCreators } from '@src/app/analytics/googleAnalytics/actions';
import { actionCreators as weakActionCreators } from '@src/app/login/weak/actions';
import { PaymentsPostbody, PaymentsPayload } from '@src/_api/payload/payments';
import { forkJoin } from 'rxjs';
import { UpdateCoreCardPostbody } from '@src/_api/payload/update-payment';

const PAYMENT_PATH = '/payment';
const CARD_UPDATE_PATH = '/cardupdate';

const getAllSucceeded = (...responses) => {
  const successResponses = responses.filter(item => 
    item.success === true);
  return successResponses;
};
const formatPostBody = (paymentPayload: PaymentsPayload) => {
  // add logic for other OMS Payment body values later
  let body: PaymentsPostbody = {
    prodIdAlias: paymentPayload.prodIdAlias,
    accountNumber: paymentPayload.accountNumber,
    invoiceNumber: paymentPayload.invoiceNumber,
    invoiceNumbers: paymentPayload.invoiceNumbers,
    invoiceAmountPaid: paymentPayload.invoiceAmountPaid,
    amountPaid: paymentPayload.amountPaid,
    effort: paymentPayload.effort,
    paymentOnOrderType: paymentPayload.paymentOnOrderType,
    creditCardExpireMonth: paymentPayload.creditCardExpireMonth,
    creditCardExpireYear: paymentPayload.creditCardExpireYear,
    creditCardLastFour: paymentPayload.creditCardLastFour,
    creditCardType: paymentPayload.creditCardType,
    creditCardNumber: paymentPayload.creditCardNumber,  
    paymentType: paymentPayload.paymentType,
    altPayInfo: paymentPayload.altPayInfo,
    authCode: paymentPayload.authCode,
    autoRenewOptin: paymentPayload.autoRenewOptIn,
    systemCode: paymentPayload.systemCode,
    coreTransactionType: 'payment',
    alternatePaymentType: paymentPayload.alternatePaymentType,
    billingAgreementId: paymentPayload.billingAgreementId,
    clientTransactionId: paymentPayload.clientTransactionId,
    authorizationDate: paymentPayload.authorizationDate,
    authorizationCode: paymentPayload.authorizationCode,
    presettleIndicator: paymentPayload.presettleIndicator,
    digitalPaymentType: paymentPayload.digitalPaymentType,
    entityIds: paymentPayload.entityIds
  };
  if (paymentPayload.systemCode === 'SERVPF') {
    body.errorSwitch = 'O';
  }
  return body;
};
export const applyPayment = (
  action$: ActionsObservable<RootAction>,
  state$: StateObservable<RootState>,
) =>
  action$.pipe(
    ofType(PAYMENTS_START),
    mergeMap(({ payload }) => {
      const clientConfig = state$.value.clientConfig.clientConfig;
      const loading = of(actionCreators.paymentsLoading(true));
      const client = new ApiClient();

      let filteredPayloads = payload.paymentsPayloads.filter(pmt => {
        return pmt.systemCode !== 'SERVPF'  || (pmt.systemCode === 'SERVPF' && !pmt.updatePayMethodOnly);
      });
      const promises = filteredPayloads.map((item: PaymentsPayload) => { 
        let body = formatPostBody(item);
        const apiConfig: ApiConfig = {
          credentialsObject: state$.value.auth.omsAuth.credentials,
          providerType: clientConfig.providerType,
          url: clientConfig.url,
          path: PAYMENT_PATH,
          region: clientConfig.Region,
          requestmethod: 'POST',
          requestbody: body,
          parms: {},
          additionalParms: {
            headers: {
              'x-session-id': clientConfig.uuid
            },
          },
        };
        return fromPromise(client.callApiGateway(apiConfig));
      });

      const source$ = forkJoin(promises, getAllSucceeded).pipe(
        map(allSucceeded => {
          if (filteredPayloads.length === allSucceeded.length) {
            payload.paymentsPayloads.forEach(pmtPayload => {
              gaActionCreators.createGAEvent({
                category: pmtPayload.googleAnalyticsCategory,
                action: 'Successful',
                value: parseFloat(pmtPayload.amountPaid) * 100,
                label: pmtPayload.googleAnalyticsLabel || pmtPayload.prodIdAlias,
              });
            });
            if (payload.paymentsPayloads[0].systemCode === 'CORE' && payload.paymentsPayloads[0].entityIds && payload.paymentsPayloads[0].entityIds.length > 0) {
              return actionCreators.paymentsAutoRenewStart({accountResponse: payload, responses: allSucceeded, success: true});
            }
            return actionCreators.paymentsSuccess({accountResponse: payload, responses: allSucceeded, success: true});
          } else {
            payload.paymentsPayloads.forEach(pmtPayload => {
              gaActionCreators.createGAEvent({
                category: pmtPayload.googleAnalyticsCategory,
                action: 'Unsuccessful',
                value:  parseFloat(pmtPayload.amountPaid) * 100,
                label: pmtPayload.googleAnalyticsLabel || pmtPayload.prodIdAlias,
              });
            });
            return actionCreators.paymentsError({
              success: false,
              message: 'Error on Payment',
            });
          }
        }),
        catchError(err => {
          gaActionCreators.createGAEvent({
            category: payload.paymentsPayloads[0].googleAnalyticsCategory,
            action: 'Unknown Error',
            label: payload.paymentsPayloads[0].googleAnalyticsLabel || payload.paymentsPayloads[0].prodIdAlias
          });
          return of(
            actionCreators.paymentsError({ success: false, message: err }),
          );
        }),
      );
      
      if (payload.paymentsPayloads[0].systemCode === 'CORE' 
          && payload.paymentsPayloads[0].entityIds 
          && payload.paymentsPayloads[0].entityIds.length > 0) {
        return concat(loading, source$);
      } 
      let reloadCust: any = null;
      const refetchCustomerPayload = {
        accountNumber: payload.paymentsPayloads[0].accountNumber,
        postalCode: payload.paymentsPayloads[0].postalCode,
      };
      reloadCust = of(weakActionCreators.refetchCustomer(refetchCustomerPayload));
      return concat(loading, source$, reloadCust);
    }),
  );

    export const startAutoRenewWithPayment =  (
      action$: ActionsObservable<RootAction>,
      state$: StateObservable<RootState>,
    ) =>
      action$.pipe(
        ofType(PAYMENTS_AUTO_RENEW_START),
        mergeMap(({ payload }) => {
        const client = new ApiClient();
        const clientConfig = state$.value.clientConfig.clientConfig;
  
        const promises = payload.accountResponse.paymentsPayloads.map(updatePaymentPayload => {
          let path = CARD_UPDATE_PATH;
          let wsgBody:  UpdateCoreCardPostbody = {
            prodIdAlias: updatePaymentPayload.prodIdAlias,
            accountNumber: updatePaymentPayload.accountNumber,
            paymentType: updatePaymentPayload.paymentType,
            billingOrganizationCode: updatePaymentPayload.billingOrganizationCode,
            creditCardNumber:  updatePaymentPayload.creditCardNumber,
            creditCardType: updatePaymentPayload.creditCardType,
            creditCardExpireMonth:  updatePaymentPayload.creditCardExpireMonth,
            creditCardExpireYear:  updatePaymentPayload.creditCardExpireYear,
            creditCardLastFour: updatePaymentPayload.creditCardLastFour,
            systemCode: updatePaymentPayload.systemCode,
            coreTransactionType: 'updatePayment',
            entityIds: updatePaymentPayload.entityIds,
            digitalPaymentType: updatePaymentPayload.digitalPaymentType,
            alternatePaymentType: updatePaymentPayload.alternatePaymentType,
            billingAgreementId: updatePaymentPayload.billingAgreementId,
          };
          const apiConfig: ApiConfig = {
            credentialsObject: state$.value.auth.omsAuth.credentials,
            providerType: clientConfig.providerType,
            url: clientConfig.url,
            path: path,
            region: clientConfig.Region,
            requestmethod: 'PUT',
            requestbody: wsgBody,
            parms: {},
            additionalParms: {
              headers: {
                'x-session-id': clientConfig.uuid
              },
            },
          };
          return fromPromise(client.callApiGateway(apiConfig));
        });
        
        const source$ = forkJoin(promises, getAllSucceeded).pipe(
          map(responses => {
            let updateCount = payload.accountResponse.paymentsPayloads.length;
            if (updateCount === responses.length) {
              payload.accountResponse.paymentsPayloads.forEach(updatePmtPayload => {
                gaActionCreators.createGAEvent({
                  category: 'Update Payment Method',
                  action: 'Successful',
                  label: updatePmtPayload.googleAnalyticsLabel,
                });
              });
              return actionCreators.paymentsSuccess({accountResponse: payload.accountResponse, responses: payload.responses, success: true});
            } else {
              payload.accountResponse.paymentsPayloads.forEach(updatePmtPayload => {
                gaActionCreators.createGAEvent({
                  category: 'Update Payment Method',
                  action: 'Unsuccessful',
                  label: updatePmtPayload.googleAnalyticsLabel,
                });
              });
              return actionCreators.paymentsError({
                success: false,
                message: 'Error on ' + 'Update Payment Method',
              });
            }
          }),
          catchError(err => {
            gaActionCreators.createGAEvent({
              category: 'Update Payment Method',
              action: 'Unknown Error',
              label: payload.accountResponse.paymentsPayloads[0].googleAnalyticsLabel,
            });
            return of(
              actionCreators.paymentsError({ success: false, message: err }),
            );
          }),
        );
        
        let reloadCust: any = null;
        const refetchCustomerPayload = {
          accountNumber: payload.accountResponse.paymentsPayloads[0].accountNumber,
          postalCode: payload.accountResponse.paymentsPayloads[0].postalCode,
        };
        reloadCust = of(weakActionCreators.refetchCustomer(refetchCustomerPayload));

        return concat(source$, reloadCust);
      }),
    );
  
export const epics = combineEpics(applyPayment, startAutoRenewWithPayment);
